// components/ScraperDashboard/ScraperMetricsPanel.js
// Comprehensive scraper metrics display from scrape runs
import React, { useState, useCallback } from 'react';
import {
  Activity, AlertCircle, CheckCircle, Clock, Shield,
  Zap, TrendingUp, Server, Eye, ChevronDown, ChevronUp,
  AlertTriangle, RefreshCw, Wifi, Target, Gauge
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './ScraperMetricsPanel.css';

// Health badge component
function HealthBadge({ health }) {
  const config = {
    healthy: { cls: 'health-success', icon: CheckCircle, label: 'Healthy' },
    warning: { cls: 'health-warning', icon: AlertTriangle, label: 'Warning' },
    degraded: { cls: 'health-warning', icon: AlertTriangle, label: 'Degraded' },
    failing: { cls: 'health-danger', icon: AlertCircle, label: 'Failing' },
    blocked: { cls: 'health-danger', icon: Shield, label: 'Blocked' },
    challenged: { cls: 'health-danger', icon: Shield, label: 'Challenged' },
  };
  const { cls, icon: Icon, label } = config[health] || config.healthy;
  return (
    <span className={`smp-health-badge ${cls}`}>
      <Icon size={12} />
      {label}
    </span>
  );
}

// Mini stat card
function MiniStat({ icon: Icon, value, label, color = 'default' }) {
  return (
    <div className={`smp-mini-stat smp-stat-${color}`}>
      <Icon size={14} className="smp-mini-icon" />
      <div className="smp-mini-body">
        <span className="smp-mini-value">{value}</span>
        <span className="smp-mini-label">{label}</span>
      </div>
    </div>
  );
}

// Progress bar for success rate
function SuccessRateBar({ rate, label }) {
  const numRate = parseInt(rate) || 0;
  const colorClass = numRate >= 95 ? 'rate-success' : numRate >= 80 ? 'rate-warning' : 'rate-danger';
  return (
    <div className="smp-rate-bar">
      <div className="smp-rate-header">
        <span className="smp-rate-label">{label}</span>
        <span className={`smp-rate-value ${colorClass}`}>{rate}</span>
      </div>
      <div className="smp-rate-track">
        <div className={`smp-rate-fill ${colorClass}`} style={{ width: `${numRate}%` }} />
      </div>
    </div>
  );
}

function ScraperMetricsPanel({ scrapeHistory, onRefresh, loading, hideHeader = false }) {
  const [selectedRun, setSelectedRun] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [runDetails, setRunDetails] = useState({});

  // Get latest run with metrics
  const latestWithMetrics = scrapeHistory?.find(r => r.total_suppliers > 0) || scrapeHistory?.[0];

  // Fetch detailed run data if needed
  const fetchRunDetails = useCallback(async (runId) => {
    if (runDetails[runId]) return runDetails[runId];
    try {
      const data = await apiClient.get(`/api/scraper/results/${runId}/suppliers`);
      setRunDetails(prev => ({ ...prev, [runId]: data }));
      return data;
    } catch (e) {
      console.error('Failed to fetch run details', e);
      return null;
    }
  }, [runDetails]);

  // Calculate aggregate metrics from recent runs
  const aggregateMetrics = React.useMemo(() => {
    if (!scrapeHistory || scrapeHistory.length === 0) return null;

    const recentRuns = scrapeHistory.slice(0, 10); // Last 10 runs
    const successfulRuns = recentRuns.filter(r => r.status === 'success');
    const totalSuppliers = recentRuns.reduce((sum, r) => sum + (parseInt(r.total_suppliers) || 0), 0);
    const totalSaved = recentRuns.reduce((sum, r) => sum + (parseInt(r.saved_to_db) || 0), 0);
    const totalDuration = recentRuns.reduce((sum, r) => sum + (parseInt(r.duration_seconds) || 0), 0);
    const avgDuration = recentRuns.length > 0 ? Math.round(totalDuration / recentRuns.length) : 0;
    const successRate = recentRuns.length > 0 ? Math.round((successfulRuns.length / recentRuns.length) * 100) : 0;
    const saveRate = totalSuppliers > 0 ? Math.round((totalSaved / totalSuppliers) * 100) : 0;

    return {
      runsAnalyzed: recentRuns.length,
      successRate,
      totalSuppliers,
      totalSaved,
      saveRate,
      avgDuration,
      successfulRuns: successfulRuns.length,
      failedRuns: recentRuns.length - successfulRuns.length,
    };
  }, [scrapeHistory]);

  if (!scrapeHistory || scrapeHistory.length === 0) {
    return (
      <div className="smp-container smp-empty">
        <Activity size={24} />
        <p>No scrape runs yet. Trigger a scrape to see metrics.</p>
      </div>
    );
  }

  return (
    <div className={`smp-container ${expanded ? 'smp-expanded' : 'smp-collapsed'}`}>
      {/* Header */}
      {!hideHeader && (
        <div className="smp-header" onClick={() => setExpanded(!expanded)}>
          <div className="smp-header-left">
            <Gauge size={16} />
            <span className="smp-title">Scraper Performance</span>
            {aggregateMetrics && (
              <span className={`smp-overall-badge ${aggregateMetrics.successRate >= 90 ? 'badge-success' : aggregateMetrics.successRate >= 70 ? 'badge-warning' : 'badge-danger'}`}>
                {aggregateMetrics.successRate}% Success
              </span>
            )}
          </div>
          <div className="smp-header-right">
            <button className="smp-refresh-btn" onClick={(e) => { e.stopPropagation(); onRefresh?.(); }} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'smp-spin' : ''} />
            </button>
            <ChevronDown size={16} className={`smp-chevron ${expanded ? 'rotated' : ''}`} />
          </div>
        </div>
      )}

      {(hideHeader || expanded) && (
        <div className="smp-body">
          {/* Aggregate Stats */}
          {aggregateMetrics && (
            <div className="smp-section">
              <h4 className="smp-section-title">Last {aggregateMetrics.runsAnalyzed} Runs</h4>
              <div className="smp-stats-grid">
                <MiniStat icon={CheckCircle} value={aggregateMetrics.successfulRuns} label="Successful" color="success" />
                <MiniStat icon={AlertCircle} value={aggregateMetrics.failedRuns} label="Failed" color={aggregateMetrics.failedRuns > 0 ? 'danger' : 'default'} />
                <MiniStat icon={Target} value={aggregateMetrics.totalSuppliers.toLocaleString()} label="Found" color="primary" />
                <MiniStat icon={Activity} value={aggregateMetrics.totalSaved.toLocaleString()} label="Saved" color="accent" />
              </div>
              <div className="smp-rates">
                <SuccessRateBar rate={`${aggregateMetrics.successRate}%`} label="Run Success Rate" />
                <SuccessRateBar rate={`${aggregateMetrics.saveRate}%`} label="Data Save Rate" />
              </div>
              <div className="smp-avg-stat">
                <Clock size={12} />
                <span>Avg Duration: <strong>{aggregateMetrics.avgDuration}s</strong></span>
              </div>
            </div>
          )}

          {/* Latest Run Details */}
          {latestWithMetrics && (
            <div className="smp-section smp-latest">
              <h4 className="smp-section-title">Latest Run</h4>
              <div className="smp-latest-header">
                <span className={`smp-status smp-status-${latestWithMetrics.status}`}>
                  {latestWithMetrics.status === 'success' ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                  {latestWithMetrics.status}
                </span>
                <span className="smp-latest-time">{new Date(latestWithMetrics.started_at).toLocaleString()}</span>
              </div>
              <div className="smp-latest-stats">
                <div className="smp-stat-row">
                  <span className="smp-stat-key">POs Captured</span>
                  <span className="smp-stat-val">{latestWithMetrics.total_suppliers || 0}</span>
                </div>
                <div className="smp-stat-row">
                  <span className="smp-stat-key">Saved to DB</span>
                  <span className="smp-stat-val">{latestWithMetrics.saved_to_db || 0}</span>
                </div>
                <div className="smp-stat-row">
                  <span className="smp-stat-key">Duration</span>
                  <span className="smp-stat-val">{latestWithMetrics.duration_seconds ? `${latestWithMetrics.duration_seconds}s` : '--'}</span>
                </div>
                <div className="smp-stat-row">
                  <span className="smp-stat-key">Pages</span>
                  <span className="smp-stat-val">{latestWithMetrics.pages_processed || '--'}</span>
                </div>
                {latestWithMetrics.total_value && (
                  <div className="smp-stat-row">
                    <span className="smp-stat-key">Total Value</span>
                    <span className="smp-stat-val smp-val-money">${parseFloat(latestWithMetrics.total_value).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                )}
              </div>
              {latestWithMetrics.criteria && (
                <div className="smp-criteria">
                  {latestWithMetrics.criteria.departmentCode && (
                    <span className="smp-criteria-chip">Dept: {latestWithMetrics.criteria.departmentCode}</span>
                  )}
                  {latestWithMetrics.criteria.acquisitionType && (
                    <span className="smp-criteria-chip">{latestWithMetrics.criteria.acquisitionType}</span>
                  )}
                  {latestWithMetrics.criteria.dateFrom && (
                    <span className="smp-criteria-chip">{latestWithMetrics.criteria.dateFrom} → {latestWithMetrics.criteria.dateTo}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Run History Mini-List */}
          <div className="smp-section">
            <h4 className="smp-section-title">Recent Runs</h4>
            <div className="smp-history-list">
              {scrapeHistory.slice(0, 5).map((run, i) => (
                <div
                  key={run.id || i}
                  className={`smp-history-item ${selectedRun === run.id ? 'selected' : ''}`}
                  onClick={() => setSelectedRun(selectedRun === run.id ? null : run.id)}
                >
                  <span className={`smp-hist-status smp-status-${run.status}`}>
                    {run.status === 'success' ? <CheckCircle size={10} /> : <AlertCircle size={10} />}
                  </span>
                  <span className="smp-hist-count">{run.total_suppliers || 0}</span>
                  <span className="smp-hist-time">{new Date(run.started_at).toLocaleDateString()}</span>
                  {run.criteria?.departmentCode && (
                    <span className="smp-hist-dept">{run.criteria.departmentCode}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScraperMetricsPanel;
